import { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Ghost, Skull, ChevronLeft, Calendar, Minus, Plus, Loader2, CheckCircle2, Mail, Download, FileDown, Clock, ShieldAlert, AlertTriangle, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { openHHWaiver } from './HHWaiverModal';
import { QRCodeSVG } from 'qrcode.react';
import WaiverStampIcon from './WaiverStampIcon';

const API = process.env.REACT_APP_BACKEND_URL;
let globalSetOpen = null;

export function openBookingModal(step) {
  const s = typeof step === 'string' ? step : 'locations';
  if (globalSetOpen) globalSetOpen(true, s);
  axios.post(`${API}/api/track/booking`).catch(() => {});
}

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

// ms until the next midnight in Asia/Tokyo (when today's advance rate for tomorrow expires)
function msUntilTokyoMidnight() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', hour12: false,
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date());
  const get = (k) => parseInt(parts.find(p => p.type === k)?.value || '0', 10);
  const h = get('hour'), m = get('minute'), s = get('second');
  // Handle the "24" edge case Intl sometimes emits
  const H = h === 24 ? 0 : h;
  const sec = (24 - H) * 3600 - m * 60 - s;
  return Math.max(0, sec) * 1000;
}

function AdvanceCountdown({ isJa, compact = false }) {
  const [ms, setMs] = useState(() => msUntilTokyoMidnight());
  useEffect(() => {
    const id = setInterval(() => setMs(msUntilTokyoMidnight()), 1000);
    return () => clearInterval(id);
  }, []);
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const label = isJa ? '事前割引の締切' : 'Deal resets in';
  return (
    <div data-testid="hh-countdown"
      className={`flex items-center justify-center gap-2 ${compact ? 'text-[10px] py-1' : 'text-[11px] py-2'} font-heading tracking-widest uppercase text-yellow-300`}>
      <Clock size={compact ? 11 : 13} className="text-yellow-400" />
      <span>{label}</span>
      <span className="font-mono text-yellow-100" data-testid="hh-countdown-value">
        {h > 0 ? `${h}h ` : ''}{pad(m)}m {pad(s)}s
      </span>
    </div>
  );
}

export default function BookingModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('locations');
  const [cfg, setCfg] = useState(null);          // site settings (halloween toggle)
  const [hh, setHh] = useState(null);            // haunted-house config from /api/hh/config
  const [type, setType] = useState('weekend');   // weekend|weekday
  const [date, setDate] = useState(null);        // YYYY-MM-DD
  const [qty, setQty] = useState(1);
  const [adults, setAdults] = useState(1);
  const [kids69, setKids69] = useState(0);
  const [kids05, setKids05] = useState(0);
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '' });
  const [hold, setHold] = useState(null);        // {hold_id, order_num, total_yen, expires_at, ...}
  const [order, setOrder] = useState(null);      // {status, tickets, ...}
  const [busy, setBusy] = useState(false);
  const [payErr, setPayErr] = useState('');
  const [acknowledgedPhysical, setAcknowledgedPhysical] = useState(false);
  globalSetOpen = (v, s) => {
    setIsOpen(v);
    if (v) {
      setStep(s || 'locations');
      setPayErr('');
      setAcknowledgedPhysical(false);
      if (s === 'halloween-ticket' || s === 'halloween-date' || s === 'halloween-disclaimer') {
        setOrder(null); setHold(null);
      }
    }
  };
  const { t, lang } = useLanguage();
  const isJa = lang === 'ja';

  // Load site settings for halloween-enabled flag
  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/api/settings/site`).then(r => setCfg(r.data)).catch(() => {});
    axios.get(`${API}/api/hh/config`).then(r => setHh(r.data)).catch(() => {});
  }, [isOpen]);

  // Pause jump-scares while the guest is on payment / QR steps — resume otherwise
  useEffect(() => {
    const paying = isOpen && (step === 'payment' || step === 'success');
    if (paying) {
      window.__HH_PAUSE_SCARES = true;
      try { window.dispatchEvent(new CustomEvent('hh-pause-scares')); } catch {}
      return () => { window.__HH_PAUSE_SCARES = false; };
    }
    window.__HH_PAUSE_SCARES = false;
  }, [isOpen, step]);

  const LOCATIONS = [
    { name: 'Rycom Mall', rooms: t('rycomRooms'), address: '〒901-2306 Okinawa, Kitanakagusuku 1 Raikamu', url: 'https://book.squareup.com/appointments/h2vlbju85siksv/location/L7GDDBBGP4SJJ/services' },
    { name: 'Okinawa City Gate 2', rooms: t('gateRooms'), address: '〒904-0004 コザ花園、Okinawa, Chūō, 1-chōme−10', url: 'https://book.squareup.com/appointments/os2cv2xxc8sgmm/location/L1NXWGXTR4G2A/services' },
  ];
  const halloweenEnabled = !!cfg?.halloween_enabled;

  // Filter dates for the ticket type (Weekday/Weekend)
  const availableDates = useMemo(() => {
    if (!hh) return [];
    return (hh.dates || []).filter(d => d.is_open && !d.is_past && d.available > 0 && d.is_weekend === (type === 'weekend'));
  }, [hh, type]);

  const fullUnitPrice = type === 'weekend' ? (hh?.weekend_price_yen ?? 0) : (hh?.weekday_price_yen ?? 0);
  const advanceUnitPrice = type === 'weekend' ? (hh?.weekend_advance_price_yen ?? fullUnitPrice) : (hh?.weekday_advance_price_yen ?? fullUnitPrice);
  const pickedDateInfo = useMemo(() => (hh?.dates || []).find(d => d.date === date), [hh, date]);
  const unitPrice = pickedDateInfo ? pickedDateInfo.effective_price_yen : fullUnitPrice;
  const isPickedAdvance = !!pickedDateInfo?.is_advance;
  const familyOn = !!hh?.family_pricing_enabled;
  const childFullPrice = hh?.child_price_yen ?? 0;
  const childAdvancePrice = hh?.child_advance_price_yen ?? childFullPrice;
  const childUnit = isPickedAdvance ? childAdvancePrice : childFullPrice;
  const totalPeople = familyOn ? (adults + kids69 + kids05) : qty;
  const total = familyOn
    ? (adults * unitPrice + kids69 * childUnit)
    : (unitPrice * qty);
  const fullTotal = familyOn
    ? (adults * fullUnitPrice + kids69 * childFullPrice)
    : (fullUnitPrice * qty);
  const canContinue = familyOn
    ? (totalPeople >= 1 && (adults >= 1 || (kids69 + kids05) === 0))
    : (qty >= 1);

  const goToDates = (t) => { setType(t); setDate(null); setStep('halloween-date'); };

  const createHold = async () => {
    setBusy(true); setPayErr('');
    try {
      const body = {
        event_date: date, ticket_type: type,
        buyer_name: buyer.name, buyer_email: buyer.email, buyer_phone: buyer.phone,
      };
      if (familyOn) {
        body.adults = adults;
        body.kids_6_9 = kids69;
        body.kids_0_5 = kids05;
        body.quantity = totalPeople; // fallback for validators
      } else {
        body.quantity = qty;
      }
      const r = await axios.post(`${API}/api/hh/hold`, body);
      setHold(r.data);
      setStep('halloween-payment');
    } catch (e) {
      setPayErr(e?.response?.data?.detail || 'Could not reserve — please try another date');
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsOpen(false)} className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }} className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div data-testid="booking-modal" className="bg-card border border-border rounded-3xl w-full max-w-md overflow-hidden shadow-[0_0_40px_rgba(227,37,16,0.2)] max-h-[90vh] flex flex-col">
              {/* LOCATIONS STEP */}
              {step === 'locations' && (
                <>
                  <Header title={t('chooseLocation')} onClose={() => setIsOpen(false)} />
                  <div className="p-6 space-y-4 overflow-y-auto">
                    {halloweenEnabled && (
                      <button type="button" onClick={() => setStep('halloween-disclaimer')} data-testid="booking-location-halloween"
                        className="w-full text-left relative flex items-start gap-4 p-5 rounded-2xl border-2 border-orange-500/60 bg-gradient-to-br from-orange-950/80 via-red-950/60 to-black hover:border-orange-400 hover:shadow-[0_0_28px_rgba(255,90,0,0.55)] transition-all group overflow-hidden">
                        <span className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-orange-500/20 blur-2xl" />
                        <span className="absolute top-1.5 right-2 text-[9px] font-heading tracking-widest uppercase text-orange-300 bg-orange-900/60 border border-orange-500/40 px-1.5 py-0.5 rounded">{isJa ? '期間限定' : 'LIMITED'}</span>
                        <div className="w-12 h-12 rounded-xl bg-orange-500/15 border border-orange-500/40 flex items-center justify-center flex-shrink-0"><Ghost size={22} className="text-orange-400" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-xl tracking-wide uppercase text-orange-200 leading-tight">{isJa ? 'ライカム モール' : 'Rycom Mall'}</p>
                          <p className="font-heading text-[13px] tracking-widest uppercase text-orange-400 font-semibold mt-0.5 flex items-center gap-1.5"><Skull size={13} />{isJa ? 'ホーンテッド ハウス チケット' : 'Haunted House Ticket'}</p>
                          <p className="font-sans text-xs text-orange-100/70 mt-1.5">{isJa ? '平日または週末チケット' : 'Choose Weekday or Weekend ticket'}</p>
                        </div>
                      </button>
                    )}
                    {LOCATIONS.map((loc, i) => (
                      <a key={loc.name} href={loc.url} target="_blank" rel="noopener noreferrer" data-testid={`booking-location-${i}`} onClick={() => setIsOpen(false)} className="flex items-start gap-4 w-full p-5 rounded-2xl bg-secondary border border-border hover:border-brand/50 hover:bg-brand/5 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0"><MapPin size={22} className="text-brand" /></div>
                        <div>
                          <p className="font-heading text-xl tracking-wide uppercase text-foreground">{loc.name}</p>
                          <p className="font-sans text-sm text-brand font-semibold">{loc.rooms}</p>
                          <p className="font-sans text-xs text-muted-foreground mt-1">{loc.address}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}

              {/* HALLOWEEN SAFETY DISCLAIMER (must be acknowledged before purchase) */}
              {step === 'halloween-disclaimer' && (
                <HalloweenDisclaimerStep
                  isJa={isJa}
                  acknowledged={acknowledgedPhysical}
                  onAcknowledgedChange={setAcknowledgedPhysical}
                  onBack={() => setStep('locations')}
                  onClose={() => setIsOpen(false)}
                  onContinue={() => setStep('halloween-ticket')}
                />
              )}

              {/* WEEKDAY/WEEKEND PICKER */}
              {step === 'halloween-ticket' && (
                <>
                  <HauntedFX />
                  <HauntedHeader onBack={() => setStep('halloween-disclaimer')} onClose={() => setIsOpen(false)} title={isJa ? 'ホーンテッド ハウス' : 'Haunted House'} />
                  <div className="relative p-6 space-y-4 overflow-y-auto overflow-x-hidden"
                    style={{
                      background:
                        'radial-gradient(120% 100% at 50% 0%, #1a0a06 0%, #0a0503 55%, #060302 100%)',
                    }}>
                    {/* atmospheric FX: bones falling + blood drips */}
                    <FallingBones count={12} />
                    <BloodDrips count={8} />

                    {/* floating pumpkin decorations behind content */}
                    <div className="pointer-events-none absolute -left-3 top-16 opacity-25 animate-pulse" style={{ animationDuration: '6s' }}><PumpkinIcon size={44} glow /></div>
                    <div className="pointer-events-none absolute right-1 top-40 opacity-20 animate-pulse" style={{ animationDuration: '7.4s' }}><PumpkinIcon size={30} /></div>
                    <div className="pointer-events-none absolute left-4 bottom-4 opacity-20 animate-pulse" style={{ animationDuration: '5.6s' }}><PumpkinIcon size={22} /></div>

                    {/* content layer sits above the FX */}
                    <div className="relative z-10 space-y-4">
                    <div className="pointer-events-none absolute -left-3 top-16 opacity-25 animate-pulse" style={{ animationDuration: '6s' }}><PumpkinIcon size={44} glow /></div>
                    <div className="pointer-events-none absolute right-1 top-40 opacity-20 animate-pulse" style={{ animationDuration: '7.4s' }}><PumpkinIcon size={30} /></div>
                    <div className="pointer-events-none absolute left-4 bottom-4 opacity-20 animate-pulse" style={{ animationDuration: '5.6s' }}><PumpkinIcon size={22} /></div>

                    <p className="relative text-center font-heading text-xs tracking-[0.3em] uppercase text-orange-300/90"
                      style={{ textShadow: '0 0 10px rgba(255,120,30,0.8)' }}>
                      {isJa ? 'チケットの種類をお選びください' : 'Choose Your Ticket'}
                    </p>

                    {hh?.has_advance_discount && (
                      <div className="relative rounded-xl border border-yellow-400/60 bg-gradient-to-r from-yellow-950/40 via-orange-950/50 to-yellow-950/40 px-3 py-2.5 text-center"
                        style={{ boxShadow: '0 0 20px rgba(255,190,40,0.35), inset 0 0 20px rgba(0,0,0,0.5)' }}>
                        <p className="font-heading text-[12px] tracking-widest uppercase text-yellow-300"
                          style={{ textShadow: '0 0 10px rgba(255,210,60,0.9)' }}>
                          {isJa
                            ? `事前購入で ${hh.advance_discount_type === 'percent' ? hh.advance_discount_value + '% OFF' : '¥' + Number(hh.advance_discount_value).toLocaleString() + ' OFF'}`
                            : `Buy Ahead & Save ${hh.advance_discount_type === 'percent' ? hh.advance_discount_value + '%' : '¥' + Number(hh.advance_discount_value).toLocaleString()}`}
                        </p>
                        <p className="text-[10px] text-yellow-200/70 mt-0.5">
                          {isJa ? '同日購入は通常料金' : 'Same-day purchases are full price'}
                        </p>
                        <AdvanceCountdown isJa={isJa} compact />
                      </div>
                    )}

                    {/* WEEKDAY CARD */}
                    <button onClick={() => goToDates('weekday')} data-testid="halloween-ticket-weekday"
                      className="group relative w-full text-left flex items-center gap-4 p-5 rounded-2xl border-2 border-orange-500/70 overflow-hidden transition-all hover:border-orange-300 hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(135deg, rgba(60,20,4,0.9) 0%, rgba(20,8,2,0.95) 100%)',
                        boxShadow: '0 0 24px rgba(255,120,20,0.35), inset 0 0 40px rgba(255,120,20,0.08)',
                      }}>
                      {/* corner pumpkin decoration */}
                      <div className="pointer-events-none absolute -right-3 -bottom-3 opacity-25 group-hover:opacity-50 transition-opacity">
                        <PumpkinIcon size={90} glow />
                      </div>
                      {/* neon border shimmer on hover */}
                      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ boxShadow: 'inset 0 0 30px rgba(255,140,30,0.35)' }} />
                      <div className="relative w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'radial-gradient(circle, rgba(255,140,30,0.25) 0%, rgba(255,120,20,0.05) 70%)',
                          border: '1px solid rgba(255,140,30,0.6)',
                          boxShadow: '0 0 12px rgba(255,140,30,0.5)',
                        }}>
                        <PumpkinIcon size={30} glow />
                      </div>
                      <div className="relative flex-1">
                        <p className="font-heading text-xl tracking-wide uppercase text-orange-100"
                          style={{ textShadow: '0 0 10px rgba(255,140,30,0.7)' }}>
                          {isJa ? '平日チケット' : 'Weekday Ticket'}
                        </p>
                        <p className="font-sans text-xs text-orange-200/70 mt-0.5">{isJa ? '月曜日 – 金曜日' : 'Monday – Friday'}</p>
                        <div className="flex items-baseline gap-2 mt-2">
                          {hh?.has_advance_discount && hh?.weekday_advance_price_yen < hh?.weekday_price_yen ? (
                            <>
                              <span className="font-heading text-[10px] tracking-widest uppercase text-yellow-300 bg-yellow-950/60 border border-yellow-500/40 px-1.5 py-0.5 rounded">
                                {isJa ? '事前' : 'Advance'}
                              </span>
                              <p className="font-heading text-lg text-orange-200"
                                style={{ textShadow: '0 0 10px rgba(255,140,30,0.9)' }}>
                                {yen(hh.weekday_advance_price_yen)}
                              </p>
                              <p className="text-xs text-orange-100/40 line-through">{yen(hh.weekday_price_yen)}</p>
                            </>
                          ) : (
                            <p className="font-heading text-lg text-orange-200"
                              style={{ textShadow: '0 0 10px rgba(255,140,30,0.9)' }}>
                              {yen(hh?.weekday_price_yen)}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronLeft size={18} className="relative rotate-180 text-orange-300 group-hover:text-orange-200 transition-transform group-hover:translate-x-1" />
                    </button>

                    {/* WEEKEND CARD */}
                    <button onClick={() => goToDates('weekend')} data-testid="halloween-ticket-weekend"
                      className="group relative w-full text-left flex items-center gap-4 p-5 rounded-2xl border-2 border-red-500/80 overflow-hidden transition-all hover:border-red-300 hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(135deg, rgba(80,10,5,0.95) 0%, rgba(40,6,3,0.95) 55%, rgba(20,4,2,0.95) 100%)',
                        boxShadow: '0 0 28px rgba(255,40,40,0.4), inset 0 0 40px rgba(255,40,40,0.08)',
                      }}>
                      {/* corner pumpkin + spider decoration */}
                      <div className="pointer-events-none absolute -right-3 -bottom-4 opacity-30 group-hover:opacity-60 transition-opacity">
                        <PumpkinIcon size={100} glow />
                      </div>
                      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ boxShadow: 'inset 0 0 30px rgba(255,40,40,0.35)' }} />
                      <div className="relative w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'radial-gradient(circle, rgba(255,40,40,0.25) 0%, rgba(255,40,40,0.05) 70%)',
                          border: '1px solid rgba(255,80,80,0.6)',
                          boxShadow: '0 0 14px rgba(255,40,40,0.6)',
                        }}>
                        <Skull size={26} className="text-red-300" style={{ filter: 'drop-shadow(0 0 6px rgba(255,40,40,0.9))' }} />
                      </div>
                      <div className="relative flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-heading text-xl tracking-wide uppercase text-red-100"
                            style={{ textShadow: '0 0 10px rgba(255,50,50,0.8)' }}>
                            {isJa ? '週末チケット' : 'Weekend Ticket'}
                          </p>
                          <span className="font-heading text-[9px] tracking-widest uppercase text-yellow-300 bg-red-900/80 border border-yellow-500/50 px-1.5 py-0.5 rounded animate-pulse"
                            style={{ animationDuration: '2.5s', textShadow: '0 0 6px rgba(255,220,60,0.9)' }}>
                            {isJa ? '本物の俳優' : 'LIVE ACTORS'}
                          </span>
                        </div>
                        <p className="font-sans text-xs text-red-200/70 mt-0.5">{isJa ? '土曜日 & 日曜日' : 'Saturday & Sunday'}</p>
                        <div className="flex items-baseline gap-2 mt-2">
                          {hh?.has_advance_discount && hh?.weekend_advance_price_yen < hh?.weekend_price_yen ? (
                            <>
                              <span className="font-heading text-[10px] tracking-widest uppercase text-yellow-300 bg-yellow-950/60 border border-yellow-500/40 px-1.5 py-0.5 rounded">
                                {isJa ? '事前' : 'Advance'}
                              </span>
                              <p className="font-heading text-lg text-red-200"
                                style={{ textShadow: '0 0 10px rgba(255,50,50,0.9)' }}>
                                {yen(hh.weekend_advance_price_yen)}
                              </p>
                              <p className="text-xs text-red-100/40 line-through">{yen(hh.weekend_price_yen)}</p>
                            </>
                          ) : (
                            <p className="font-heading text-lg text-red-200"
                              style={{ textShadow: '0 0 10px rgba(255,50,50,0.9)' }}>
                              {yen(hh?.weekend_price_yen)}
                            </p>
                          )}
                        </div>
                      </div>
                      <ChevronLeft size={18} className="relative rotate-180 text-red-300 group-hover:text-red-200 transition-transform group-hover:translate-x-1" />
                    </button>

                    <p className="relative text-center text-[10px] font-heading tracking-[0.25em] uppercase text-orange-300/50 pt-1">
                      {isJa ? '18歳以上推奨・自己責任にてご入場ください' : 'Enter if you dare · 18+ recommended'}
                    </p>
                    </div>{/* /content z-10 */}
                  </div>
                </>
              )}

              {/* DATE PICKER */}
              {step === 'halloween-date' && (
                <>
                  <StepHeader onBack={() => setStep('halloween-ticket')} onClose={() => setIsOpen(false)} title={isJa ? '日付を選択' : 'Pick a Date'} />
                  <div className="p-6 space-y-3 overflow-y-auto">
                    <p className="text-xs text-muted-foreground">
                      {type === 'weekend' ? (isJa ? '週末チケット (土日)' : 'Weekend Ticket (Sat/Sun)') : (isJa ? '平日チケット (月-金)' : 'Weekday Ticket (Mon–Fri)')}
                    </p>
                    {hh?.has_advance_discount && (
                      <div className="rounded-lg border border-yellow-500/40 bg-yellow-950/20">
                        <AdvanceCountdown isJa={isJa} compact />
                      </div>
                    )}
                    {availableDates.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-6">{isJa ? '空き日程がありません' : 'No available dates.'}</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {availableDates.map(d => {
                          const active = date === d.date;
                          const lowStock = d.available <= 20;
                          const isAdv = d.is_advance;
                          const savings = d.savings_yen || 0;
                          return (
                            <button key={d.date} data-testid={`hh-pick-date-${d.date}`} onClick={() => { setDate(d.date); setStep('halloween-qty'); }}
                              className={`relative p-3 rounded-xl border-2 text-center transition-all ${active ? 'border-orange-400 bg-orange-950/40' : 'border-border hover:border-orange-500/50'}`}>
                              {isAdv && savings > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 font-heading text-[9px] tracking-widest uppercase bg-yellow-400 text-black px-1.5 py-0.5 rounded-full shadow">
                                  {isJa ? `-¥${savings.toLocaleString()}` : `SAVE ¥${savings.toLocaleString()}`}
                                </span>
                              )}
                              <div className="font-heading text-base">{d.date.slice(8)}</div>
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(d.date + 'T00:00:00').toLocaleDateString(isJa ? 'ja-JP' : 'en-US', { weekday: 'short' })}</div>
                              <div className="mt-1 leading-tight">
                                {isAdv && savings > 0 ? (
                                  <div className="flex flex-col items-center">
                                    <span className="text-[11px] font-heading text-orange-300">{yen(d.effective_price_yen)}</span>
                                    <span className="text-[9px] text-muted-foreground line-through">{yen(d.full_price_yen)}</span>
                                  </div>
                                ) : (
                                  <span className="text-[11px] font-heading text-neutral-300">{yen(d.effective_price_yen)}</span>
                                )}
                              </div>
                              <div className={`text-[10px] mt-1 ${lowStock ? 'text-orange-400' : 'text-emerald-400'}`}>{d.available} {isJa ? '席' : 'left'}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* QTY + BUYER */}
              {step === 'halloween-qty' && date && (
                <>
                  <StepHeader onBack={() => setStep('halloween-date')} onClose={() => setIsOpen(false)} title={isJa ? '数量と情報' : 'Guests & Info'} />
                  <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="text-center">
                      <p className="font-heading text-lg">{date}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {type} •{' '}
                        {isPickedAdvance && unitPrice < fullUnitPrice ? (
                          <>
                            <span className="text-orange-300 font-semibold">{yen(unitPrice)}</span>{' '}
                            <span className="line-through text-muted-foreground">{yen(fullUnitPrice)}</span>{' '}
                            {isJa ? '/名' : 'each'}
                          </>
                        ) : (
                          <>{yen(unitPrice)} {isJa ? 'per guest' : 'each'}</>
                        )}
                      </p>
                      {isPickedAdvance && unitPrice < fullUnitPrice && (
                        <p className="mt-1 inline-block font-heading text-[10px] tracking-widest uppercase bg-yellow-400 text-black px-2 py-0.5 rounded-full">
                          {isJa ? `事前購入割引適用` : `Advance-Purchase Discount Applied`}
                        </p>
                      )}
                    </div>
                    {!familyOn && (
                      <>
                        <div className="flex items-center justify-center gap-4">
                          <button onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="hh-qty-minus" className="w-12 h-12 rounded-full border border-border hover:border-orange-500 flex items-center justify-center"><Minus size={18} /></button>
                          <div className="font-heading text-4xl w-16 text-center" data-testid="hh-qty-value">{qty}</div>
                          <button onClick={() => setQty(q => Math.min(20, q + 1))} data-testid="hh-qty-plus" className="w-12 h-12 rounded-full border border-border hover:border-orange-500 flex items-center justify-center"><Plus size={18} /></button>
                        </div>
                      </>
                    )}

                    {familyOn && (
                      <div className="space-y-3" data-testid="hh-family-picker">
                        {/* ADULTS */}
                        <FamilyRow
                          label={isJa ? '大人' : 'Adults'}
                          sub={isJa ? '10歳以上' : 'Ages 10+'}
                          price={unitPrice}
                          fullPrice={fullUnitPrice}
                          isAdvance={isPickedAdvance}
                          value={adults}
                          min={0} max={20}
                          onChange={setAdults}
                          testid="hh-family-adults"
                        />
                        {/* KIDS 6-9 */}
                        <FamilyRow
                          label={isJa ? '子供 6–9歳' : 'Kids 6–9'}
                          sub={isJa ? '大人同伴' : 'With an adult'}
                          price={childUnit}
                          fullPrice={childFullPrice}
                          isAdvance={isPickedAdvance}
                          value={kids69}
                          min={0} max={20}
                          onChange={setKids69}
                          testid="hh-family-kids69"
                        />
                        {/* KIDS 0-5 FREE */}
                        <FamilyRow
                          label={isJa ? '子供 0–5歳' : 'Kids 0–5'}
                          sub={isJa ? '無料・大人同伴・自己責任' : 'FREE · adult required · at your own risk'}
                          price={0}
                          fullPrice={0}
                          isAdvance={false}
                          free
                          value={kids05}
                          min={0} max={20}
                          onChange={setKids05}
                          testid="hh-family-kids05"
                        />
                        {kids05 > 0 && (
                          <div className="rounded-lg border border-red-700/60 bg-red-950/40 px-3 py-2 text-[11px] text-red-200 flex items-start gap-2">
                            <Skull size={13} className="text-red-400 mt-0.5 shrink-0" />
                            <span>{isJa
                              ? '⚠ 0–5歳は無料ですが、ホーンテッドハウスの内容は幼児には過激です。大人の判断でご入場ください。'
                              : "⚠ Under-6 is FREE but the haunted house is intense — bring them at your own discretion. An adult must accompany them at all times."}</span>
                          </div>
                        )}
                        {(kids69 + kids05) > 0 && adults < 1 && (
                          <p className="text-[11px] text-orange-400 text-center">
                            {isJa ? '子供の入場には大人1名以上が必要です' : 'At least one adult is required with any child'}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="text-center" data-testid="hh-qty-total">
                      <div className="text-[10px] font-heading tracking-widest uppercase text-muted-foreground">
                        {familyOn ? (isJa ? `${totalPeople}名合計` : `${totalPeople} guest${totalPeople === 1 ? '' : 's'} · Total`) : (isJa ? '合計' : 'Total')}
                      </div>
                      <div className="font-heading text-3xl text-orange-400"
                        style={{ textShadow: '0 0 10px rgba(255,140,30,0.6)' }}>
                        {yen(total)}
                      </div>
                      {isPickedAdvance && total < fullTotal && (
                        <div className="text-[11px] mt-0.5">
                          <span className="line-through text-muted-foreground">{yen(fullTotal)}</span>
                          <span className="ml-2 text-yellow-300 font-heading tracking-widest uppercase">
                            {isJa ? `¥${(fullTotal - total).toLocaleString()} お得` : `Save ¥${(fullTotal - total).toLocaleString()}`}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <input placeholder={isJa ? 'お名前' : 'Name'} value={buyer.name} onChange={e => setBuyer({ ...buyer, name: e.target.value })} data-testid="hh-buyer-name"
                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
                      <input type="email" placeholder={isJa ? 'メールアドレス' : 'Email'} value={buyer.email} onChange={e => setBuyer({ ...buyer, email: e.target.value })} data-testid="hh-buyer-email"
                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
                      <input placeholder={isJa ? '電話番号' : 'Phone (optional)'} value={buyer.phone} onChange={e => setBuyer({ ...buyer, phone: e.target.value })} data-testid="hh-buyer-phone"
                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
                    </div>
                    {payErr && <p className="text-xs text-red-400 text-center">{String(payErr)}</p>}
                    <button onClick={createHold} disabled={busy || !buyer.name || !canContinue} data-testid="hh-continue-to-pay"
                      className="w-full bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
                      {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                      {isJa ? '支払いへ進む' : 'Continue to Payment'}
                    </button>
                    <p className="text-[10px] text-center text-muted-foreground">{isJa ? '席は10分間確保されます' : 'Seats are held for 10 minutes'}</p>
                  </div>
                </>
              )}

              {/* PAYMENT */}
              {step === 'halloween-payment' && hold && (
                <PaymentStep
                  hold={hold} hh={hh} isJa={isJa}
                  onBack={() => setStep('halloween-qty')}
                  onClose={() => setIsOpen(false)}
                  onSuccess={(ord) => { setOrder(ord); setStep('halloween-success'); }}
                />
              )}

              {/* SUCCESS + TICKETS */}
              {step === 'halloween-success' && order && (
                <SuccessStep order={order} isJa={isJa} onClose={() => setIsOpen(false)} />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Header({ title, onClose }) {
  return (
    <div className="bg-brand p-6 flex items-center justify-between shrink-0">
      <h3 className="font-heading text-2xl tracking-wide uppercase text-white">{title}</h3>
      <button onClick={onClose} data-testid="booking-modal-close" className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"><X size={16} /></button>
    </div>
  );
}
function FamilyRow({ label, sub, price, fullPrice, isAdvance, free, value, min = 0, max = 20, onChange, testid }) {
  const showStrike = isAdvance && price < fullPrice && !free;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-orange-500/30 bg-black/40 px-3 py-2.5" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-sm tracking-wide uppercase text-orange-100">{label}</div>
        <div className="text-[11px] text-muted-foreground">{sub}</div>
        <div className="text-xs mt-0.5">
          {free ? (
            <span className="font-heading tracking-widest uppercase text-emerald-400">FREE</span>
          ) : showStrike ? (
            <>
              <span className="font-heading text-orange-300">{`¥${Number(price).toLocaleString()}`}</span>
              <span className="ml-1 text-muted-foreground line-through">{`¥${Number(fullPrice).toLocaleString()}`}</span>
              <span className="ml-1 text-[10px] text-muted-foreground">each</span>
            </>
          ) : (
            <>
              <span className="font-heading text-orange-300">{`¥${Number(price).toLocaleString()}`}</span>
              <span className="ml-1 text-[10px] text-muted-foreground">each</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
          data-testid={`${testid}-minus`}
          className="w-9 h-9 rounded-full border border-border hover:border-orange-500 flex items-center justify-center disabled:opacity-40"
          disabled={value <= min}>
          <Minus size={14} />
        </button>
        <div className="font-heading text-xl w-8 text-center" data-testid={`${testid}-value`}>{value}</div>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
          data-testid={`${testid}-plus`}
          className="w-9 h-9 rounded-full border border-border hover:border-orange-500 flex items-center justify-center disabled:opacity-40"
          disabled={value >= max}>
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

function StepHeader({ onBack, onClose, title }) {
  return (
    <div className="bg-gradient-to-r from-orange-700 via-red-700 to-orange-800 p-6 flex items-center justify-between shrink-0">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-white/90 hover:text-white font-heading text-xs tracking-widest uppercase"><ChevronLeft size={16} />Back</button>
      <h3 className="font-heading text-lg sm:text-xl tracking-wide uppercase text-white text-center flex-1 -ml-4">{title}</h3>
      <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"><X size={16} /></button>
    </div>
  );
}

// SVG jack-o'-lantern — sized via width prop, glow-friendly
function PumpkinIcon({ className = '', size = 28, glow = false }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden style={glow ? { filter: 'drop-shadow(0 0 6px rgba(255,140,20,0.9))' } : undefined}>
      {/* stem */}
      <path d="M31 8 c-3 -2 -7 -1 -7 5 c0 3 2 4 4 4 c-1 -3 1 -5 5 -5 c2 -1 2 -3 -2 -4z" fill="#4c8a2b" />
      {/* body: multiple lobes for a real pumpkin silhouette */}
      <ellipse cx="18" cy="38" rx="13" ry="20" fill="#ff7a1a" />
      <ellipse cx="46" cy="38" rx="13" ry="20" fill="#ff7a1a" />
      <ellipse cx="32" cy="38" rx="20" ry="22" fill="#ff8c1a" />
      {/* highlights */}
      <ellipse cx="22" cy="30" rx="4" ry="10" fill="#ffb063" opacity="0.55" />
      <ellipse cx="44" cy="30" rx="3" ry="8" fill="#ffb063" opacity="0.45" />
      {/* face: triangle eyes + jagged grin (glowing) */}
      <g fill="#fff2b0">
        <polygon points="22,34 28,34 25,40" />
        <polygon points="36,34 42,34 39,40" />
        <path d="M20 46 L26 48 L28 46 L32 49 L36 46 L38 48 L44 46 L42 51 L38 52 L36 50 L32 52 L28 50 L26 52 L22 51 Z" />
      </g>
    </svg>
  );
}

// Falling / rotating bone + skull SVGs
function BoneShape({ variant = 'bone', size = 22 }) {
  if (variant === 'skull') {
    return (
      <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.4))' }}>
        <path d="M16 3c-6 0-10 4-10 10 0 3 1 5 3 7v3c0 2 2 3 4 3h6c2 0 4-1 4-3v-3c2-2 3-4 3-7 0-6-4-10-10-10z" fill="#f0ede1" />
        <circle cx="11" cy="14" r="2.6" fill="#0b0b0b" />
        <circle cx="21" cy="14" r="2.6" fill="#0b0b0b" />
        <path d="M15 21 l-1 3 M17 21 l1 3 M16 20 l0 4" stroke="#0b0b0b" strokeWidth="1" strokeLinecap="round" />
      </svg>
    );
  }
  if (variant === 'rib') {
    return (
      <svg viewBox="0 0 40 20" width={size * 1.4} height={size * 0.7} aria-hidden style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.3))' }}>
        <path d="M6 10 Q6 4 12 4 H28 Q34 4 34 10 Q34 16 28 16 H12 Q6 16 6 10 Z" fill="none" stroke="#f0ede1" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  // long bone (femur)
  return (
    <svg viewBox="0 0 44 16" width={size * 1.6} height={size * 0.6} aria-hidden style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.35))' }}>
      <path d="M4 8 c0 -3 3 -6 6 -6 c3 0 4 2 4 4 h16 c0 -2 1 -4 4 -4 c3 0 6 3 6 6 c0 3 -3 6 -6 6 c-3 0 -4 -2 -4 -4 h-16 c0 2 -1 4 -4 4 c-3 0 -6 -3 -6 -6 z" fill="#f0ede1" />
    </svg>
  );
}

function FallingBones({ count = 10 }) {
  // Precompute deterministic-but-varied positions/timings once per mount
  const items = useMemo(() => {
    const kinds = ['bone', 'rib', 'skull', 'bone', 'rib'];
    return Array.from({ length: count }).map((_, i) => ({
      key: i,
      left: `${(i * 97 + 13) % 96}%`,
      variant: kinds[i % kinds.length],
      size: 16 + (i % 4) * 5,
      delay: (i * 0.83) % 6,
      duration: 5 + ((i * 0.9) % 4),  // 5–9s
      rotate: 360 + (i % 3) * 180,
      opacity: 0.55 + ((i * 13) % 40) / 100,
    }));
  }, [count]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid="hh-falling-bones">
      {items.map(it => (
        <div key={it.key}
          className="absolute -top-8"
          style={{
            left: it.left,
            animation: `hh-bone-fall ${it.duration}s linear ${it.delay}s infinite`,
            opacity: it.opacity,
            transform: 'translate3d(0,0,0)',
            willChange: 'transform',
            ['--rot']: `${it.rotate}deg`,
          }}>
          <div style={{ animation: `hh-bone-spin ${it.duration * 0.7}s linear ${it.delay}s infinite` }}>
            <BoneShape variant={it.variant} size={it.size} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BloodDrips({ count = 8 }) {
  const drips = useMemo(() => (
    Array.from({ length: count }).map((_, i) => ({
      key: i,
      left: `${(i * 79 + 6) % 94}%`,
      delay: (i * 1.3) % 5,
      duration: 3.5 + ((i * 0.7) % 3),
      height: 40 + ((i * 17) % 60),
      width: 3 + (i % 3),
    }))
  ), [count]);
  return (
    <div className="pointer-events-none absolute top-0 left-0 right-0 h-full overflow-hidden" aria-hidden data-testid="hh-blood-drips">
      {drips.map(d => (
        <div key={d.key}
          className="absolute top-0"
          style={{
            left: d.left,
            width: d.width,
            height: 0,
            background: 'linear-gradient(180deg, transparent 0%, #7a0a0a 20%, #b30f0f 60%, #ff1a1a 100%)',
            borderBottomLeftRadius: '50%',
            borderBottomRightRadius: '50%',
            filter: 'drop-shadow(0 0 4px rgba(255,20,20,0.55))',
            animation: `hh-drip ${d.duration}s ease-in ${d.delay}s infinite`,
            ['--drip-max-h']: `${d.height}px`,
          }}
        />
      ))}
    </div>
  );
}

// Inject the keyframes once
function HauntedFX() {
  return (
    <style>{`
      @keyframes hh-bone-fall {
        0%   { transform: translateY(-40px); }
        100% { transform: translateY(120%); }
      }
      @keyframes hh-bone-spin {
        0%   { transform: rotate(0deg); }
        100% { transform: rotate(var(--rot, 360deg)); }
      }
      @keyframes hh-drip {
        0%   { height: 0; opacity: 0; }
        10%  { opacity: 1; }
        60%  { height: var(--drip-max-h, 80px); opacity: 1; }
        90%  { height: var(--drip-max-h, 80px); opacity: 1; }
        100% { height: var(--drip-max-h, 80px); opacity: 0; transform: translateY(20px); }
      }
      @keyframes hh-flicker {
        0%,100% { opacity: 1; }
        45%     { opacity: 0.6; }
        50%     { opacity: 1; }
        55%     { opacity: 0.7; }
      }
    `}</style>
  );
}

function HauntedHeader({ onBack, onClose, title }) {
  return (
    <div
      className="relative overflow-hidden shrink-0"
      style={{
        background:
          'radial-gradient(120% 100% at 50% 0%, #ff5b1a 0%, #b51f0a 45%, #3a0a04 100%)',
      }}
    >
      {/* subtle noise + moon glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(60% 80% at 15% 20%, rgba(255,180,80,0.35), transparent 60%), radial-gradient(50% 70% at 85% 30%, rgba(255,60,20,0.25), transparent 60%)',
        }}
      />
      {/* drifting pumpkins */}
      <div className="absolute top-2 left-3 opacity-80 animate-pulse" style={{ animationDuration: '3.4s' }}>
        <PumpkinIcon size={22} glow />
      </div>
      <div className="absolute top-3 right-14 opacity-80 animate-pulse" style={{ animationDuration: '4.1s' }}>
        <PumpkinIcon size={18} glow />
      </div>
      <div className="absolute bottom-1 left-1/3 opacity-70 animate-pulse" style={{ animationDuration: '5.2s' }}>
        <PumpkinIcon size={14} glow />
      </div>

      <div className="relative p-6 flex items-center justify-between">
        <button onClick={onBack} data-testid="hh-header-back"
          className="inline-flex items-center gap-1 text-white/90 hover:text-white font-heading text-xs tracking-widest uppercase">
          <ChevronLeft size={16} />Back
        </button>
        <h3 className="font-heading text-lg sm:text-2xl tracking-widest uppercase text-white text-center flex-1 -ml-4"
          style={{ textShadow: '0 0 12px rgba(255,140,30,0.9), 0 0 3px rgba(0,0,0,0.9)' }}>
          {title}
        </h3>
        <button onClick={onClose} data-testid="hh-header-close"
          className="w-8 h-8 rounded-full bg-black/40 border border-orange-400/40 flex items-center justify-center text-white hover:bg-black/60">
          <X size={16} />
        </button>
      </div>

      {/* drippy blood/goo bottom edge */}
      <svg viewBox="0 0 400 22" preserveAspectRatio="none" className="block w-full h-5 -mb-px" aria-hidden>
        <path
          d="M0 0 H400 V6 Q380 22 360 8 Q340 22 320 10 Q305 22 290 8 Q270 22 250 10 Q230 22 210 6 Q190 22 170 10 Q150 22 130 8 Q110 22 90 10 Q70 22 50 8 Q30 22 10 10 Q3 22 0 12 Z"
          fill="#0a0a0a"
        />
      </svg>
    </div>
  );
}

// =====================================================================
// Halloween Safety Disclaimer — must be acknowledged before booking.
// Content mirrors the physical poster so guests know the house REQUIRES
// crawling, has uneven footing, and ends with a step-down at the exit.
// =====================================================================
function HalloweenDisclaimerStep({ isJa, acknowledged, onAcknowledgedChange, onBack, onClose, onContinue }) {
  return (
    <>
      <HauntedHeader onBack={onBack} onClose={onClose} title={isJa ? '予約前にお読みください' : 'Before You Book'} />
      <div
        className="relative p-4 space-y-4 overflow-y-auto overflow-x-hidden"
        style={{
          background:
            'radial-gradient(120% 100% at 50% 0%, #0a1a06 0%, #050a03 55%, #020402 100%)',
        }}
      >
        {/* The physical safety poster — exact same visual guests see on-site */}
        <div
          className="relative rounded-xl overflow-hidden"
          style={{
            border: '1.5px solid rgba(195,255,59,0.5)',
            boxShadow: '0 0 24px rgba(163,255,45,0.3), inset 0 0 20px rgba(163,255,45,0.08)',
          }}
        >
          <img
            src="/haunted-safety-poster.jpg"
            alt={isJa ? '安全上の注意ポスター' : 'Haunted house safety poster'}
            data-testid="hh-disclaimer-poster"
            className="block w-full h-auto select-none"
            draggable={false}
          />
        </div>

        {/* Confirmation checkbox */}
        <label
          htmlFor="hh-disclaimer-check"
          data-testid="hh-disclaimer-check-label"
          className="relative flex items-start gap-3 rounded-xl p-3 cursor-pointer select-none"
          style={{
            border: '1.5px solid rgba(195,255,59,0.6)',
            background: 'linear-gradient(135deg, rgba(20,40,8,0.6), rgba(6,12,3,0.9))',
            boxShadow: acknowledged
              ? '0 0 24px rgba(163,255,45,0.45), inset 0 0 20px rgba(163,255,45,0.1)'
              : '0 0 10px rgba(163,255,45,0.15)',
          }}
        >
          <input
            id="hh-disclaimer-check"
            data-testid="hh-disclaimer-check"
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => onAcknowledgedChange(e.target.checked)}
            className="mt-0.5 w-5 h-5 shrink-0 accent-lime-400 cursor-pointer"
          />
          <div className="flex-1 text-[12px] leading-snug text-lime-50">
            {isJa
              ? '私は上記のポスター内容を理解し、這って進むこと、段差・暗闇・ジャンプスケアを含む身体的な体験に自己責任で参加することに同意します。'
              : 'I have read the poster above and understand the house requires crawling, uneven footing, darkness, and jump scares. I confirm I am physically able to crawl and I accept these conditions.'}
          </div>
        </label>

        {/* Action buttons */}
        <div className="relative grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={onContinue}
            disabled={!acknowledged}
            data-testid="hh-disclaimer-continue"
            className="w-full font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={
              acknowledged
                ? {
                    background: 'linear-gradient(90deg, #86d900, #c3ff3b, #86d900)',
                    color: '#0a1a06',
                    boxShadow: '0 0 24px rgba(163,255,45,0.65)',
                  }
                : { background: '#1f2937', color: '#9ca3af' }
            }
          >
            <CheckCircle2 size={16} />
            {isJa ? '同意して続ける' : 'I Agree — Continue'}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="hh-disclaimer-cancel"
            className="w-full bg-transparent border border-neutral-700 hover:border-red-500 hover:text-red-300 text-neutral-400 font-heading tracking-widest uppercase text-xs py-2.5 rounded-full"
          >
            {isJa ? 'キャンセル' : "I'm Not Able To — Go Back"}
          </button>
        </div>

        <p className="relative text-center text-[10px] text-neutral-500">
          {isJa ? '18歳以上推奨・自己責任にてご入場ください' : 'Enter if you dare · 18+ recommended'}
        </p>
      </div>
    </>
  );
}

// =====================================================================
// Payment Step — Square Web Payments SDK
// =====================================================================
function PaymentStep({ hold, hh, isJa, onBack, onClose, onSuccess }) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(hold.expires_at) - Date.now()) / 1000)));
  const cardRef = useRef(null);

  // Countdown
  useEffect(() => {
    const t = setInterval(() => {
      setRemaining(r => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Load Square SDK
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        if (!hh?.square_configured) {
          setErr('Payments are not configured. Ask the venue to complete Square setup.');
          return;
        }
        if (cardRef.current) return; // already attached from a prior render
        // Inject SDK script if not present
        const src = hh.square_env === 'production'
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        if (!document.querySelector('script[data-square-sdk]')) {
          const s = document.createElement('script');
          s.src = src; s.async = true; s.dataset.squareSdk = '1';
          document.head.appendChild(s);
        }
        // Poll for window.Square to become available (script onload can fire before symbol is set)
        for (let i = 0; i < 60; i++) {
          if (window.Square) break;
          await new Promise(r => setTimeout(r, 100));
        }
        if (cancelled) return;
        if (!window.Square) throw new Error('Square.js failed to load — check network / adblockers');
        const payments = window.Square.payments(hh.square_app_id, hh.square_location_id);
        const card = await payments.card();
        if (cancelled) { try { card.destroy(); } catch {} return; }
        await card.attach('#hh-card-container');
        cardRef.current = card;
        setReady(true);
        setErr('');
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load payment form');
      }
    }
    init();
    return () => {
      cancelled = true;
      try { cardRef.current?.destroy?.(); cardRef.current = null; } catch {}
    };
  }, [hh?.square_configured, hh?.square_env, hh?.square_app_id, hh?.square_location_id]);

  const pay = async () => {
    setBusy(true); setErr('');
    try {
      // Simple card charge (no 3DS/SCA needed for JPY Sandbox).
      // tokenize() with no args returns a card nonce; amount/currency are set
      // server-side by /api/hh/pay from the hold record.
      const tok = await cardRef.current.tokenize();
      if (tok.status !== 'OK') {
        throw new Error((tok.errors && tok.errors[0]?.message) || 'Card tokenization failed');
      }
      const r = await axios.post(`${API}/api/hh/pay`, {
        hold_id: hold.hold_id,
        source_id: tok.token,
      });
      // Poll order until MINTED (webhook may take a moment)
      let tries = 0;
      while (tries < 20) {
        const or = await axios.get(`${API}/api/hh/order/${hold.hold_id}`);
        if (or.data?.tickets?.length > 0) {
          onSuccess(or.data);
          return;
        }
        await new Promise(r => setTimeout(r, 800));
        tries++;
      }
      throw new Error('Payment took too long to confirm — check the admin dashboard.');
    } catch (e) {
      setErr(e?.response?.data?.detail?.square_errors?.[0]?.detail || e.message || 'Payment failed');
    } finally { setBusy(false); }
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <>
      <StepHeader onBack={onBack} onClose={onClose} title={isJa ? 'お支払い' : 'Payment'} />
      <div className="p-6 space-y-4 overflow-y-auto">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>{hold.event_date} • {hold.ticket_type} × {hold.quantity}</div>
          <div className="font-mono">{mm}:{ss}</div>
        </div>
        <div className="font-heading text-3xl text-center text-orange-400">{yen(hold.total_yen)}</div>
        <div id="hh-card-container" data-testid="hh-card-container" className="min-h-[110px] rounded-lg border border-border p-2 bg-white text-black" />
        {err && <p className="text-xs text-red-400" data-testid="hh-pay-error">{err}</p>}
        <button onClick={pay} disabled={!ready || busy || remaining === 0} data-testid="hh-pay-submit"
          className="w-full bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={16} /> : null}
          {busy ? (isJa ? '処理中…' : 'Processing…') : (isJa ? `${yen(hold.total_yen)}を支払う` : `Pay ${yen(hold.total_yen)}`)}
        </button>
        <p className="text-[10px] text-center text-muted-foreground">
          {hh?.square_env === 'sandbox'
            ? 'Sandbox mode — use test card 4111 1111 1111 1111, any future date, CVV 111'
            : 'Payments are processed securely by Square'}
        </p>
      </div>
    </>
  );
}

// =====================================================================
// Dedicated Ticket Viewer window — pops out into its own window so the
// customer can keep the QR visible without dismissing the booking modal.
// =====================================================================
function buildTicketViewerHtml(order, isJa) {
  const tickets = order?.tickets || [];
  const orderNum = order?.order_num || '';
  const orderIdent = order?.hold_id || order?.order_id || order?.order_num || '';
  const apiBase = API || '';
  const eventDate = order?.event_date || '';
  const ticketType = order?.ticket_type || '';
  const qty = order?.quantity || tickets.length;
  const roleLabel = (role) => {
    if (role === 'kid_6_9') return isJa ? '子供 6–9' : 'Kid 6–9';
    if (role === 'kid_0_5') return isJa ? '子供 0–5・無料' : 'Kid 0–5 · Free';
    return isJa ? '大人' : 'Adult';
  };
  const roleColor = (role) => {
    if (role === 'kid_0_5') return '#4b5563';
    if (role === 'kid_6_9') return '#15803d';
    return '#c2410c';
  };
  const cards = tickets.map((tk) => `
    <div class="ticket" data-admission="${tk.admission_number}">
      <img class="qr" src="${apiBase}/api/hh/order/${encodeURIComponent(orderIdent)}/ticket-png?admission=${tk.admission_number}"
           alt="ticket ${tk.admission_number}"
           onclick="saveTicket('${tk.admission_number}')"
           style="cursor:pointer"
           title="${isJa ? 'タップして保存' : 'Tap to save'}"
           onerror="this.onerror=null;this.src='${tk.qr_data_uri}';this.style.aspectRatio='1/1';" />
      <div class="admission">#${tk.admission_number}</div>
      <button class="save-btn" onclick="saveTicket('${tk.admission_number}')">
        ${isJa ? 'タップして画像を保存' : 'Tap to Save Image'}
      </button>
    </div>
  `).join('');
  const saveScript = `
    var API_BASE = ${JSON.stringify(apiBase)};
    var ORDER_IDENT = ${JSON.stringify(orderIdent)};
    var ORDER_NUM = ${JSON.stringify(orderNum)};
    async function saveTicket(admission){
      var btn = document.querySelector('[data-admission="'+admission+'"] .save-btn');
      var oldLabel = btn ? btn.innerText : '';
      if (btn) { btn.disabled = true; btn.innerText = '…'; }
      var filename = 'OkiEscape-' + ORDER_NUM + '-' + ('00'+admission).slice(-3) + '.png';
      var url = API_BASE + '/api/hh/order/' + encodeURIComponent(ORDER_IDENT) + '/ticket-png?admission=' + admission + '&download=1';
      function blobDownload(blob){
        var objUrl = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = objUrl; a.download = filename; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function(){ URL.revokeObjectURL(objUrl); }, 10000);
      }
      try {
        var res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error('ticket-png http '+res.status);
        var blob = await res.blob();
        // Try Web Share for Photos save on iOS/Android
        if (navigator.canShare && navigator.share) {
          try {
            var file = new File([blob], filename, { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], title: 'Ticket #'+admission, text: 'OKI-ESCAPE Haunted House Ticket #'+admission });
              return;
            }
          } catch(err){
            if (err && err.name === 'AbortError') return;
            // fall through to blob download
          }
        }
        blobDownload(blob);
      } catch(err) {
        // Absolute last fallback — try to download the raw QR img that's already on the page
        var img = document.querySelector('[data-admission="'+admission+'"] .qr');
        if (img && img.src) {
          var a2 = document.createElement('a');
          a2.href = img.src; a2.download = filename;
          document.body.appendChild(a2); a2.click(); a2.remove();
        }
      } finally {
        if (btn) { btn.disabled = false; btn.innerText = oldLabel; }
      }
    }
    // Warn on accidental close
    window.addEventListener('beforeunload', function(e){
      e.preventDefault();
      e.returnValue = '';
    });
  `;
  return `<!DOCTYPE html>
<html lang="${isJa ? 'ja' : 'en'}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${isJa ? 'チケットビューア' : 'Ticket Viewer'} — ${orderNum}</title>
<style>
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: radial-gradient(ellipse at top, #1a0505 0%, #0a0202 55%, #000 100%);
    color: #fff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 100vh;
    padding: 20px;
  }
  .wrap { max-width: 520px; margin: 0 auto; }
  header {
    background: linear-gradient(90deg, #047857, #059669, #047857);
    border-radius: 18px;
    padding: 18px 20px;
    box-shadow: 0 0 40px rgba(227,37,16,0.25);
    margin-bottom: 18px;
    text-align: center;
  }
  header h1 {
    margin: 0;
    font-size: 15px;
    letter-spacing: 3px;
    text-transform: uppercase;
    font-weight: 700;
  }
  .sub {
    text-align: center;
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #f97316;
    margin-bottom: 6px;
  }
  .order-info {
    text-align: center;
    font-size: 13px;
    color: #fef3c7;
    letter-spacing: 1.5px;
    margin-bottom: 18px;
    font-weight: 600;
  }
  .tickets {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 18px;
  }
  .ticket {
    background: #fff;
    color: #000;
    border-radius: 14px;
    padding: 10px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
  }
  .role-badge {
    display: inline-block;
    color: #fff;
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 6px;
    margin-bottom: 10px;
    font-weight: 700;
  }
  .qr { width: 100%; aspect-ratio: 2.75 / 1; display: block; object-fit: contain; border-radius: 10px; }
  .admission {
    margin-top: 8px;
    font-size: 12px;
    letter-spacing: 3px;
    text-transform: uppercase;
    font-weight: 700;
  }
  .save-btn {
    margin-top: 10px;
    width: 100%;
    background: #000;
    color: #fff;
    border: none;
    padding: 8px;
    border-radius: 8px;
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    cursor: pointer;
    font-weight: 700;
  }
  .save-btn:hover { background: #262626; }
  .note {
    text-align: center;
    font-size: 10px;
    color: #a3a3a3;
    letter-spacing: 1px;
    margin-top: 12px;
  }
  .close-hint {
    text-align: center;
    font-size: 10px;
    color: #fbbf24;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-bottom: 14px;
    padding: 10px;
    background: rgba(251,191,36,0.08);
    border: 1px solid rgba(251,191,36,0.25);
    border-radius: 10px;
  }
</style>
</head>
<body>
  <div class="wrap">
    <header>
      <h1>${isJa ? 'チケットビューア' : 'Ticket Viewer'}</h1>
    </header>
    <div class="sub">${isJa ? '入口でスタッフにQRを見せてください' : 'Show these QR codes at the door'}</div>
    <div class="order-info">${orderNum} • ${eventDate} • ${ticketType} × ${qty}</div>
    <div class="close-hint">${isJa ? 'このウィンドウは開いたままにしておいてください。元のページも開いています。' : 'Keep this window open — your booking page is still open too.'}</div>
    <div class="tickets">${cards}</div>
    <div class="note">${isJa ? 'QRコードは一度きり有効です。少なくとも1枚は保存してください。' : 'Each QR is single-use — save at least one copy.'}</div>
  </div>
  <script>${saveScript}</script>
</body>
</html>`;
}

// Opens the ticket viewer in a dedicated popup window (falls back to a new
// browser tab if the popup is blocked). The original booking modal stays
// mounted so the QR remains accessible even if the popup is closed.
function openTicketViewerWindow(order, isJa) {
  if (!order || !(order.tickets || []).length) return null;
  const html = buildTicketViewerHtml(order, isJa);
  const winName = `oki-ticket-${order.order_num || order.order_id || Date.now()}`;
  const features = [
    'popup=yes',
    'width=560',
    'height=820',
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  let win = null;
  try { win = window.open('', winName, features); } catch (_) { win = null; }
  if (!win) {
    // Popup blocked — fall back to a new tab
    try { win = window.open('', '_blank'); } catch (_) { win = null; }
  }
  if (!win) return null;
  try {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  } catch (_) {
    return null;
  }
  return win;
}

// =====================================================================
// Success Step
// =====================================================================
function SuccessStep({ order, isJa, onClose }) {
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState('');
  const [emailInput, setEmailInput] = useState(order?.buyer_email || '');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState('');
  const [viewerBlocked, setViewerBlocked] = useState(false);
  const viewerRef = useRef(null);
  const autoOpenedRef = useRef(false);

  // Auto-open dedicated ticket viewer window once on mount
  useEffect(() => {
    if (autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    // Slight delay so the browser doesn't confuse it with the Square redirect
    const id = setTimeout(() => {
      const w = openTicketViewerWindow(order, isJa);
      if (w) {
        viewerRef.current = w;
        setViewerBlocked(false);
      } else {
        setViewerBlocked(true);
      }
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openViewerAgain = () => {
    // If a viewer window is already open, refocus it rather than duplicating
    if (viewerRef.current && !viewerRef.current.closed) {
      try { viewerRef.current.focus(); return; } catch (_) {}
    }
    const w = openTicketViewerWindow(order, isJa);
    if (w) {
      viewerRef.current = w;
      setViewerBlocked(false);
    } else {
      setViewerBlocked(true);
    }
  };

  const pdfUrl = `${API}/api/hh/order/${order.hold_id || order.order_id}/pdf`;
  const canShareFiles = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function' && typeof navigator.share === 'function';

  // Convert data-URI -> File so we can use the native share sheet on mobile
  const dataUriToFile = (uri, filename, mime = 'image/png') => {
    const b64 = uri.split(',')[1] || '';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], filename, { type: mime });
  };

  const saveTicketImage = async (tk) => {
    setSaveBusy(tk.admission_number);
    const filename = `OkiEscape-${order.order_num}-${String(tk.admission_number).padStart(3, '0')}.png`;
    const ident = order.hold_id || order.order_id || order.order_num;
    const ticketPngUrl = `${API}/api/hh/order/${encodeURIComponent(ident)}/ticket-png?admission=${tk.admission_number}&download=1`;
    const downloadBlob = (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };
    try {
      const res = await fetch(ticketPngUrl, { credentials: 'omit' });
      if (!res.ok) throw new Error(`ticket-png fetch failed (${res.status})`);
      const blob = await res.blob();
      // Prefer native share on iOS/Android so users land in Photos / Messenger
      if (canShareFiles) {
        try {
          const file = new File([blob], filename, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `OKI-ESCAPE Ticket #${tk.admission_number}`,
              text: `OKI-ESCAPE Haunted House · Ticket #${tk.admission_number}`,
            });
            return;
          }
        } catch (shareErr) {
          if (shareErr && shareErr.name === 'AbortError') return; // user cancelled
          // Any other share error — fall through to blob download
        }
      }
      // Universal fallback — direct download via blob URL
      downloadBlob(blob);
    } catch (e) {
      // Absolute last-resort fallback: open the raw QR in a new tab
      try {
        const win = window.open('', '_blank');
        if (win) win.document.write(`<title>Ticket #${tk.admission_number}</title><img src="${tk.qr_data_uri}" style="width:100%;max-width:480px;display:block;margin:40px auto" /><p style="text-align:center;font-family:sans-serif">Press and hold the image → Save Image.</p>`);
      } catch (_) { /* nothing we can do */ }
    } finally { setSaveBusy(''); }
  };

  const savePdf = async (opts = { share: false }) => {
    setPdfBusy(true);
    const filename = `OkiEscape-${order.order_num || 'tickets'}.pdf`;
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error(`PDF fetch failed (${res.status})`);
      const blob = await res.blob();
      if (opts.share && canShareFiles) {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename, text: `OKI-ESCAPE Tickets ${order.order_num || ''}` });
          return;
        }
      }
      // Save to Files / Downloads via blob URL
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      window.alert(isJa ? 'PDFの保存に失敗しました。もう一度お試しください。' : 'PDF save failed — please try again.');
    } finally { setPdfBusy(false); }
  };

  const sendEmail = async () => {
    if (!emailInput || !emailInput.includes('@')) {
      window.alert(isJa ? '有効なメールアドレスを入力してください' : 'Please enter a valid email address');
      return;
    }
    setEmailing(true);
    try {
      const target = order.hold_id || order.order_id;
      await axios.post(`${API}/api/hh/order/${target}/email`, { email: emailInput });
      setEmailSent(emailInput);
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      window.alert(String(detail).includes('not configured') || String(detail).includes('Email not configured')
        ? (isJa ? 'メール送信は現在準備中です。QRを保存またはPDFをダウンロードしてください。' : 'Email is not yet configured. Please Save the QR image or Download the PDF instead.')
        : (detail || (isJa ? 'メールの送信に失敗しました。QRを保存してください。' : 'Email failed — please save the QR image instead.')));
    } finally { setEmailing(false); }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 p-6 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="text-white" size={22} />
          <h3 className="font-heading text-lg tracking-wide uppercase text-white">{isJa ? 'ご購入ありがとうございます' : 'Payment Confirmed'}</h3>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white"><X size={16} /></button>
      </div>
      <div className="p-6 space-y-4 overflow-y-auto">
        <p className="text-center text-xs text-muted-foreground">{isJa ? 'このQRコードを入り口でスタッフに見せてください' : 'Show these QR codes to staff at the door'}</p>
        <div className="text-center font-heading text-sm">{order.order_num} • {order.event_date} • {order.ticket_type} × {order.quantity}</div>

        {/* Pop-out Ticket Viewer — dedicated window keeps QRs visible */}
        <button
          type="button"
          onClick={openViewerAgain}
          data-testid="hh-open-ticket-viewer"
          className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full shadow-[0_0_20px_rgba(249,115,22,0.35)]"
        >
          <ExternalLink size={14} />
          {isJa ? 'チケットビューアを開く' : 'Open Ticket Viewer'}
        </button>
        {viewerBlocked && (
          <div
            data-testid="hh-viewer-blocked"
            className="rounded-xl border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-[11px] leading-relaxed p-3 text-center"
          >
            {isJa
              ? 'ポップアップがブロックされました。上のボタンをもう一度タップするか、ブラウザのポップアップを許可してください。QRはこのページでもご覧いただけます。'
              : 'Popup was blocked. Tap the button above to try again — or allow popups. Your QR codes remain visible here as well.'}
          </div>
        )}

        <div className="space-y-3">
          {(order.tickets || []).map(tk => {
            const ident = order.hold_id || order.order_id || order.order_num;
            const carnivalUrl = `${API}/api/hh/order/${encodeURIComponent(ident)}/ticket-png?admission=${tk.admission_number}`;
            return (
              <div key={tk.token} data-testid={`hh-ticket-${tk.admission_number}`} className="rounded-xl bg-black/40 border border-orange-500/30 p-2 space-y-2">
                {/* Carnival ticket preview — tap the image OR the button to save */}
                <button
                  type="button"
                  onClick={() => saveTicketImage(tk)}
                  disabled={saveBusy === tk.admission_number}
                  data-testid={`hh-ticket-image-${tk.admission_number}`}
                  className="block w-full p-0 border-0 bg-transparent cursor-pointer disabled:cursor-wait"
                  title={isJa ? 'タップして保存' : 'Tap to save'}
                >
                  <img
                    src={carnivalUrl}
                    alt={`ticket ${tk.admission_number}`}
                    loading="lazy"
                    data-testid={`hh-ticket-carnival-${tk.admission_number}`}
                    onError={(e) => { e.currentTarget.src = tk.qr_data_uri; e.currentTarget.className = 'w-full aspect-square bg-white rounded-lg'; }}
                    className="w-full rounded-lg block"
                    style={{ aspectRatio: '2.75 / 1', objectFit: 'contain' }}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => saveTicketImage(tk)}
                  disabled={saveBusy === tk.admission_number}
                  data-testid={`hh-save-ticket-${tk.admission_number}`}
                  className="inline-flex items-center justify-center gap-1.5 w-full bg-orange-600 hover:bg-orange-500 text-black text-xs font-heading tracking-widest uppercase py-2 rounded-full disabled:opacity-60"
                >
                  {saveBusy === tk.admission_number
                    ? <Loader2 className="animate-spin" size={12} />
                    : <Download size={12} />}
                  {canShareFiles
                    ? (isJa ? `#${String(tk.admission_number).padStart(3,'0')} を保存 · タップ可能` : `Save Ticket #${String(tk.admission_number).padStart(3,'0')} — Tap Image or Here`)
                    : (isJa ? `#${String(tk.admission_number).padStart(3,'0')} の画像を保存` : `Save Ticket #${String(tk.admission_number).padStart(3,'0')} as Image`)}
                </button>
              </div>
            );
          })}
        </div>

        {/* WAIVER — required prior to entry */}
        <WaiverSectionForOrder order={order} isJa={isJa} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => savePdf({ share: false })}
            disabled={pdfBusy}
            data-testid="hh-download-pdf"
            className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full disabled:opacity-60"
          >
            {pdfBusy ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />}
            {isJa ? 'PDFを保存' : 'Save PDF to Files'}
          </button>
          {canShareFiles && (
            <button
              type="button"
              onClick={() => savePdf({ share: true })}
              disabled={pdfBusy}
              data-testid="hh-share-pdf"
              className="inline-flex items-center justify-center gap-2 bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white font-heading tracking-widest uppercase text-sm py-3 rounded-full disabled:opacity-60"
            >
              <FileDown size={14} />
              {isJa ? 'PDFを共有' : 'Share PDF'}
            </button>
          )}
        </div>

        {!showEmailForm && !emailSent && (
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            data-testid="hh-show-email"
            className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white font-heading tracking-widest uppercase text-sm py-3 rounded-full"
          >
            <Mail size={14} />{isJa ? 'メールで送る' : 'Email me the tickets'}
          </button>
        )}
        {showEmailForm && !emailSent && (
          <div className="rounded-xl border border-neutral-700 bg-black/40 p-3 space-y-2">
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-400">{isJa ? '送信先メールアドレス' : 'Send to'}</label>
            <input
              type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
              data-testid="hh-email-input"
              placeholder="you@example.com"
              className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-500"
            />
            <div className="flex gap-2">
              <button
                onClick={sendEmail} disabled={emailing} data-testid="hh-email-send"
                className="flex-1 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-xs py-2 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1"
              >
                {emailing ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} />}
                {emailing ? (isJa ? '送信中…' : 'Sending…') : (isJa ? '送信' : 'Send')}
              </button>
              <button onClick={() => setShowEmailForm(false)} className="px-3 text-xs text-neutral-400 hover:text-white">Cancel</button>
            </div>
          </div>
        )}
        {emailSent && (
          <div className="rounded-xl border border-emerald-700/50 bg-emerald-950/30 p-3 text-center text-xs">
            <CheckCircle2 className="inline mr-1 text-emerald-400" size={14} />
            {isJa ? `${emailSent} に送信しました` : `Sent to ${emailSent}`}
          </div>
        )}

        <p className="text-[10px] text-center text-muted-foreground">{isJa ? 'QRコードは一度きり有効です' : 'Each QR is single-use — save at least one copy.'}</p>
      </div>
    </>
  );
}

// =====================================================================
// Waiver required-prior-to-entry section shown on the booking success screen
// =====================================================================
function WaiverSectionForOrder({ order, isJa }) {
  const [status, setStatus] = useState(order?.waiver_status || 'missing');
  // Deep-link URL that guests can scan or open on any device (works even after they close the modal)
  const waiverLink = useMemo(() => {
    const ident = order?.hold_id || order?.order_id || order?.order_num || '';
    if (typeof window === 'undefined') return '';
    return ident
      ? `${window.location.origin}/haunted/waiver?order=${encodeURIComponent(ident)}`
      : `${window.location.origin}/haunted/waiver`;
  }, [order]);

  const refresh = async () => {
    try {
      const ident = order?.hold_id || order?.order_id || order?.order_num;
      if (!ident) return;
      const r = await axios.get(`${API}/api/hh/waiver/order/${encodeURIComponent(ident)}`);
      setStatus(r.data?.waiver_status || 'missing');
    } catch {}
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const openModal = () => {
    openHHWaiver({
      hold_id: order?.hold_id,
      order_id: order?.order_id,
      order_num: order?.order_num,
      buyer_name: order?.buyer_name,
      buyer_email: order?.buyer_email,
      buyer_phone: order?.buyer_phone,
    });
    // Re-check status shortly after the modal likely closes
    setTimeout(refresh, 2500);
    setTimeout(refresh, 8000);
  };

  const isSigned = status === 'signed';
  const isPending = status === 'pending_guardian';

  return (
    <div
      data-testid="hh-success-waiver-section"
      className={`rounded-2xl border-2 p-4 space-y-3 ${
        isSigned
          ? 'border-emerald-500/60 bg-emerald-950/30'
          : isPending
            ? 'border-yellow-500/60 bg-yellow-950/30'
            : 'border-red-600 bg-red-950/40'
      }`}
      style={!isSigned && !isPending ? { boxShadow: '0 0 20px rgba(220,38,38,0.35)' } : undefined}
    >
      <div className="flex items-center gap-2">
        {isSigned ? <CheckCircle2 className="text-emerald-400" size={18} /> : <AlertTriangle className="text-red-400" size={18} />}
        <div className="flex-1">
          <p className="font-heading text-xs sm:text-sm tracking-widest uppercase">
            {isSigned
              ? (isJa ? '同意書サイン済み' : 'Waiver Signed')
              : isPending
                ? (isJa ? '保護者の承認待ち' : 'Awaiting Guardian Approval')
                : (isJa ? '入場前に必須の同意書' : 'Waiver Required Prior to Entry')}
          </p>
          {!isSigned && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {isJa
                ? 'グループリーダー1名がグループ全員を代表して署名します。'
                : 'One group leader signs on behalf of the whole group.'}
            </p>
          )}
        </div>
      </div>

      {!isSigned && (
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* QR to the waiver link — guests can scan with another device */}
          <div className="shrink-0 bg-white rounded-lg p-2">
            <QRCodeSVG value={waiverLink} size={104} level="M" includeMargin={false} />
          </div>
          <div className="flex-1 min-w-0 w-full">
            <button
              type="button"
              onClick={openModal}
              data-testid="hh-success-open-waiver"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-sm py-3 rounded-full"
            >
              <WaiverStampIcon size={22} />
              {isJa ? '同意書に署名' : 'Sign Waiver Now'}
            </button>
            <a
              href={waiverLink}
              target="_blank" rel="noreferrer"
              data-testid="hh-success-waiver-link"
              className="mt-2 block text-center text-[11px] font-mono break-all text-red-300 hover:text-red-200 underline"
            >
              {waiverLink}
            </a>
            <p className="mt-2 text-[10px] text-center text-muted-foreground">
              {isJa ? 'QRを別の端末でスキャンするか、リンクをタップしてください' : 'Scan the QR with another device — or tap the link.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
